import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  editLotteryBrand,
  getAllLotteryBrands,
} from "../../../../redux/features/lotteryBrand/lotteryBrandService";
import { showToast } from "../../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";

const EditLotteryBrands = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { lotteries } = useSelector((state) => state.lottery.allLotteryBrands);

  const { isEditingLotteryBrand } = useSelector(
    (state) => state.lottery.editedLotteryBrand
  );

  const [selectedLotteryBrand, setSelectedLotteryBrand] = useState({
    name: "",
    code: "",
    logo: null,
    sampleImage: null,
  });

  const [previewLogo, setPreviewLogo] = useState(null);
  const [previewSampleImage, setPreviewSampleImage] = useState(null);

  const [errorMessages, setErrorMessages] = useState({
    name: "",
    code: "",
    logo: "",
    sampleImage: "",
  });
  const [disabledBtn, setBtnDisable] = useState(false);
  const fileInputRefLogo = useRef(null);
  const fileInputRefSample = useRef(null);
  const [initialLottery, setInitialLottery] = useState(null);

  useEffect(() => {
    dispatch(getAllLotteryBrands({}));
  }, [dispatch]);

  useEffect(() => {
    if (lotteries && id) {
      const foundLottery = lotteries.find((lottery) => lottery.id === id);
      if (foundLottery) {
        setSelectedLotteryBrand({
          ...foundLottery,
        });

        setInitialLottery({ ...foundLottery });
        if (foundLottery.logo_url) {
          setPreviewLogo(foundLottery.logo_url);
        }
        if (foundLottery.sample_url) {
          setPreviewSampleImage(foundLottery.sample_url);
        }
      }
    }
  }, [lotteries, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedLotteryBrand((prev) => ({ ...prev, [name]: value }));
    setErrorMessages((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (event, fieldName) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      setSelectedLotteryBrand((prev) => ({ ...prev, [fieldName]: file }));
      setErrorMessages((prev) => ({ ...prev, [fieldName]: "" }));

      const fileURL = URL.createObjectURL(file);
      if (fieldName === "logo") setPreviewLogo(fileURL);
      if (fieldName === "sampleImage") setPreviewSampleImage(fileURL);
    }
  };

  const handleDrop = (event, fieldName) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];

    if (!validateFile(file)) return;

    setSelectedLotteryBrand((prev) => ({ ...prev, [fieldName]: file }));
    const fileURL = URL.createObjectURL(file);
    if (fieldName === "logo") setPreviewLogo(fileURL);
    if (fieldName === "sampleImage") setPreviewSampleImage(fileURL);
  };

  const validateFile = (file) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      showToast("error", "Invalid file format. Please upload PNG or JPG.");
      return false;
    }
    return true;
  };

  function getChangedValues(selectedData) {
    const changedValues = {};
    if (!initialLottery) {
      return {};
    }
    for (const key in selectedData) {
      if (
        key !== "id" &&
        selectedData.hasOwnProperty(key) &&
        selectedData[key] !== initialLottery[key]
      ) {
        changedValues[key] = selectedData[key];
      }

      if (key === "id") {
        changedValues[key] = selectedData[key];
      }
    }
    return changedValues;
  }

  const updateLotteryBrand = async () => {
    const changedValues = getChangedValues(selectedLotteryBrand);
    const hasChanges = Object.keys(changedValues).some((key) => key !== "id");

    if (!hasChanges) {
      showToast("error", "There are no changes to update.");
    } else {
      setBtnDisable(true);
      try {
        const response = await dispatch(
          editLotteryBrand({ id, ...changedValues })
        ).unwrap();
        setBtnDisable(null);
        setInitialLottery({ ...selectedLotteryBrand });
        showToast("success", response.message);
      } catch (error) {
        setBtnDisable(false);
        showToast("error", error.message);
      }
    }
  };

  return (
    <div className={`card-section`}>
      <div className={`main-card`}>
        <h1 className={`form-title`}>Edit Lottery</h1>
        <div className={styles.formContainer}>
          <form className={styles.coordinatorForm} noValidate>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="full_name" className={`form-label`}>
                    Name<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={selectedLotteryBrand.name || ""}
                    onChange={handleInputChange}
                    placeholder="Enter Lottery Brand Name"
                    className={`form-input-default`}
                  />
                  {errorMessages.name && (
                    <p className={`text-danger`}>{errorMessages.name}</p>
                  )}
                </div>
              </div>
            </div>

            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="code" className={`form-label`}>
                    Code<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={selectedLotteryBrand.code}
                    onChange={handleInputChange}
                    placeholder="Enter Lottery Brand Code"
                    className={`form-input-default`}
                  />
                  {errorMessages.code && (
                    <p className={`text-danger`}>{errorMessages.code}</p>
                  )}
                </div>
              </div>
            </div>

            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="full_name" className={`form-label`}>
                    Logo<span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, "logo")}
                    onDragOver={(event) => event.preventDefault()}
                    onClick={() => fileInputRefLogo.current.click()}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      ref={fileInputRefLogo}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "logo")}
                      style={{ display: "none" }}
                    />
                    {previewLogo ? (
                      <div className={styles.insertedFile}>
                        <div className={styles.fileCard}>
                          <img
                            src={previewLogo}
                            alt="Logo"
                            className={styles.thumbnailImg}
                          />
                        </div>
                      </div>
                    ) : (
                      <p>Logo</p>
                    )}
                  </div>
                  {errorMessages.logo && (
                    <p className={`text-danger`}>{errorMessages.logo}</p>
                  )}
                </div>
              </div>
            </div>

            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="full_name" className={`form-label`}>
                    Sample Lottery Image
                    <span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, "sampleImage")}
                    onDragOver={(event) => event.preventDefault()}
                    onClick={() => fileInputRefSample.current.click()}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      ref={fileInputRefSample}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "sampleImage")}
                      style={{ display: "none" }}
                    />
                    {previewSampleImage ? (
                      <div className={styles.insertedFile}>
                        <div className={styles.fileCard}>
                          <img
                            src={previewSampleImage}
                            alt="Sample"
                            className={styles.thumbnailImg}
                          />
                        </div>
                      </div>
                    ) : (
                      <p>Sample Lottery Image</p>
                    )}
                  </div>
                  {errorMessages.sampleImage && (
                    <p className={`text-danger`}>{errorMessages.sampleImage}</p>
                  )}
                </div>
              </div>
            </div>

            <button
              className={`btn-primary`}
              type="button"
              disabled={disabledBtn}
              onClick={updateLotteryBrand}
            >
              {isEditingLotteryBrand ? <BeatLoader grade="#ffffff" /> : `Save`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditLotteryBrands;
